"use client";

import { cn } from "@newxsystems/shared";
import { Icon } from "@templates/design-system";
import { useId, useState } from "react";
import { DaylightRichText } from "../DaylightRichText/DaylightRichText";
import type { IAccordionListProps } from "./AccordionList.props";

export const AccordionList = ({ items, openBehavior }: IAccordionListProps) => {
  const firstOpen = items.findIndex((item) => item.initiallyOpen);
  const [openItems, setOpenItems] = useState<number[]>(
    openBehavior === "multiple-open"
      ? items.flatMap((item, index) => (item.initiallyOpen ? [index] : []))
      : firstOpen >= 0
        ? [firstOpen]
        : [],
  );
  const baseId = useId();
  const toggle = (index: number) =>
    setOpenItems((current) =>
      current.includes(index)
        ? current.filter((item) => item !== index)
        : openBehavior === "multiple-open"
          ? [...current, index]
          : [index],
    );

  return (
    <div className="border-t border-border">
      {items.map((item, index) => {
        const open = openItems.includes(index);
        const triggerId = `${baseId}-trigger-${index}`;
        const panelId = `${baseId}-panel-${index}`;
        return (
          <div className="border-b border-border" key={index}>
            <h3>
              <button
                aria-controls={panelId}
                aria-expanded={open}
                className="flex min-h-16 w-full cursor-pointer items-center gap-6 py-5 text-left transition-colors hover:text-primary focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
                id={triggerId}
                onClick={() => toggle(index)}
                type="button"
              >
                <span className="daylight-label w-8 shrink-0 text-northsea">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="flex-1 font-daylight-display text-xl font-semibold tracking-[-0.02em]">
                  {item.title}
                </span>
                <Icon
                  className={cn(
                    "size-9 shrink-0 border border-border p-2 text-northsea transition-[transform,color,border-color] duration-200",
                    open && "rotate-45 border-primary text-primary",
                  )}
                  name="plus"
                />
              </button>
            </h3>
            <div
              aria-labelledby={triggerId}
              className="grid"
              hidden={!open}
              id={panelId}
              role="region"
            >
              <DaylightRichText
                className="max-w-[65ch] pb-7 pl-14 text-base text-northsea"
                content={item.content}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
};

AccordionList.displayName = "Content::AccordionList";
